import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import RichTextEditor from '../components/RichTextEditor';
import { signatureAssetMergeMap } from '../lib/signatureAssets';
import {
    ChevronLeft, Plus, Save, Users, Mail, Send, Clock, Eye,
    MessageSquare, CheckCircle, Sparkles, Copy, Trash2, Variable, X,
    PenLine, FileSignature, Settings2, Star, FileText, Layers, Lock
} from 'lucide-react';

const SEQ_VISIBILITY_OPTIONS = [
    { value: 'private', label: 'Private' },
    { value: 'shared', label: 'Shared' },
    { value: 'organization', label: 'Organisation-wide' },
];
const SEQ_VISIBILITY_LABEL = {
    private: 'Private', shared: 'Shared', organization: 'Organisation-wide',
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emailStatusColors = {
    scheduled: 'bg-stone-100 text-stone-600',
    sent: 'bg-blue-50 text-blue-700',
    opened: 'bg-emerald-50 text-emerald-700',
    responded: 'bg-violet-50 text-violet-700',
    bounced: 'bg-red-50 text-red-600',
    complained: 'bg-orange-50 text-orange-600',
};

const emailStatusIcons = {
    scheduled: Clock,
    sent: Send,
    opened: Eye,
    responded: MessageSquare,
};

const MERGE_VARS = [
    { value: '{{FirstName}}', label: 'First Name' },
    { value: '{{LastName}}', label: 'Last Name' },
    { value: '{{CompanyName}}', label: 'Company Name' },
    { value: '{{Title}}', label: 'Title' },
    { value: '{{Department}}', label: 'Department' },
];

export default function SequenceEditor() {
    const { sequenceId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader, user } = useAuth();
    const [seq, setSeq] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedContactIdx, setSelectedContactIdx] = useState(0);
    const [selectedEmailIdx, setSelectedEmailIdx] = useState(0);
    const [contacts, setContacts] = useState([]);
    const [showAddContact, setShowAddContact] = useState(false);
    const [crmContacts, setCrmContacts] = useState([]);
    const [showDuplicate, setShowDuplicate] = useState(false);
    const [saving, setSaving] = useState(false);

    // Email form state for the right panel
    const [emailForm, setEmailForm] = useState({ subject: '', body: '', scheduled_date: '', status: 'scheduled' });
    
    // Signature management state
    const [signatures, setSignatures] = useState([]);
    const [adminTemplates, setAdminTemplates] = useState([]);
    const [showSignatureManager, setShowSignatureManager] = useState(false);
    const [editingSignature, setEditingSignature] = useState(null);
    const [showSignatureForm, setShowSignatureForm] = useState(false);
    const [signatureForm, setSignatureForm] = useState({ name: '', content: '', is_default: false });
    const [savingSignature, setSavingSignature] = useState(false);
    
    // Email templates state
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [adminEmailTemplates, setAdminEmailTemplates] = useState([]);
    const [showTemplatesBrowser, setShowTemplatesBrowser] = useState(false);
    const [templateFilter, setTemplateFilter] = useState('all');

    // 7b-ii — sequence visibility (3-tier) + shared-with member picker
    const [orgMembers, setOrgMembers] = useState([]);
    const [savingVisibility, setSavingVisibility] = useState(false);
    const [showSharePicker, setShowSharePicker] = useState(false);

    const loadSequence = useCallback(async () => {
        try {
            const headers = getAuthHeader();
            const res = await axios.get(`${API}/sequences/${sequenceId}`, { headers });
            setSeq(res.data);
            syncEmailForm(res.data, selectedContactIdx, selectedEmailIdx);
        } catch { toast.error('Failed to load sequence'); }
        finally { setLoading(false); }
    }, [sequenceId]);

    useEffect(() => { loadSequence(); }, [loadSequence]);
    
    // Load signatures on mount
    useEffect(() => {
        const loadSignatures = async () => {
            try {
                const res = await axios.get(`${API}/signatures`, { headers: getAuthHeader() });
                setSignatures(res.data.signatures || []);
                setAdminTemplates(res.data.admin_templates || []);
            } catch (err) {
                console.error('Failed to load signatures:', err);
            }
        };
        loadSignatures();
    }, []);
    
    // Load email templates on mount
    useEffect(() => {
        const loadEmailTemplates = async () => {
            try {
                const res = await axios.get(`${API}/email-templates`, { headers: getAuthHeader() });
                setEmailTemplates(res.data.templates || []);
                setAdminEmailTemplates(res.data.admin_templates || []);
            } catch (err) {
                console.error('Failed to load email templates:', err);
            }
        };
        loadEmailTemplates();
    }, []);

    // Load org members for the "Shared" visibility picker (7b-ii)
    useEffect(() => {
        const loadMembers = async () => {
            try {
                const res = await axios.get(`${API}/organizations/members`, { headers: getAuthHeader() });
                setOrgMembers(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Failed to load org members:', err);
            }
        };
        loadMembers();
    }, []);

    const syncEmailForm = (seqData, cIdx, eIdx) => {
        const contact = seqData?.contacts?.[cIdx];
        const email = contact?.emails?.[eIdx];
        if (email) {
            setEmailForm({
                subject: email.subject || '',
                body: email.body || '',
                scheduled_date: email.scheduled_date || '',
                status: email.status || 'scheduled',
            });
        } else {
            setEmailForm({ subject: '', body: '', scheduled_date: '', status: 'scheduled' });
        }
    };

    const selectContact = (idx) => {
        setSelectedContactIdx(idx);
        setSelectedEmailIdx(0);
        if (seq) syncEmailForm(seq, idx, 0);
    };

    const selectEmail = (idx) => {
        setSelectedEmailIdx(idx);
        if (seq) syncEmailForm(seq, selectedContactIdx, idx);
    };

    const handleSaveSequence = async () => {
        if (!seq) return;
        setSaving(true);
        try {
            await axios.put(`${API}/sequences/${sequenceId}`, { name: seq.name, status: seq.status, start_date: seq.start_date }, { headers: getAuthHeader() });
            toast.success('Sequence saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    // 7b-ii — locked sequences are read-only for everyone but their owner (admins override).
    const isOwner = !!seq && (
        (seq.owner_id && seq.owner_id === user?.user_id) ||
        (seq.created_by && seq.created_by === user?.user_id)
    );
    const canEditVisibility = !seq?.locked || isOwner || !!(user?.is_admin || user?.is_super_admin);

    // PUT {visibility, shared_with?} and patch local state. Used by both the
    // visibility <select> and the shared-with picker.
    const saveVisibility = async (patch) => {
        if (!seq) return;
        setSavingVisibility(true);
        try {
            await axios.put(`${API}/sequences/${sequenceId}`, patch, { headers: getAuthHeader() });
            setSeq(p => ({ ...p, ...patch }));
            toast.success('Visibility updated');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update visibility');
        } finally {
            setSavingVisibility(false);
        }
    };

    const handleVisibilityChange = (v) => {
        // Clear shared_with when leaving the "shared" tier.
        const patch = v === 'shared' ? { visibility: v } : { visibility: v, shared_with: [] };
        saveVisibility(patch);
        if (v === 'shared') setShowSharePicker(true);
    };

    const toggleShareMember = (uid) => {
        const current = Array.isArray(seq?.shared_with) ? seq.shared_with : [];
        const next = current.includes(uid) ? current.filter(x => x !== uid) : [...current, uid];
        saveVisibility({ shared_with: next });
    };

    const handleAddContact = async (contactId) => {
        try {
            const res = await axios.post(`${API}/sequences/${sequenceId}/contacts`, { contact_id: contactId }, { headers: getAuthHeader() });
            setSeq(res.data);
            setShowAddContact(false);
            toast.success('Contact added');
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add contact'); }
    };

    const handleRemoveContact = async (scId) => {
        if (!window.confirm('Remove this contact?')) return;
        try {
            const res = await axios.delete(`${API}/sequences/${sequenceId}/contacts/${scId}`, { headers: getAuthHeader() });
            setSeq(res.data);
            if (selectedContactIdx >= (res.data.contacts || []).length) {
                setSelectedContactIdx(Math.max(0, (res.data.contacts || []).length - 1));
                setSelectedEmailIdx(0);
            }
            toast.success('Contact removed');
        } catch { toast.error('Failed to remove'); }
    };

    const handleAddEmail = async () => {
        const contact = seq?.contacts?.[selectedContactIdx];
        if (!contact) return;
        try {
            const res = await axios.post(`${API}/sequences/${sequenceId}/contacts/${contact.sc_id}/emails`, {
                subject: '', body: '', scheduled_date: '',
            }, { headers: getAuthHeader() });
            setSeq(res.data);
            const newEmails = res.data.contacts[selectedContactIdx]?.emails || [];
            setSelectedEmailIdx(newEmails.length - 1);
            syncEmailForm(res.data, selectedContactIdx, newEmails.length - 1);
            toast.success('Email added');
        } catch { toast.error('Failed to add email'); }
    };

    const handleSaveEmail = async () => {
        const contact = seq?.contacts?.[selectedContactIdx];
        const email = contact?.emails?.[selectedEmailIdx];
        if (!contact || !email) return;
        try {
            const res = await axios.put(
                `${API}/sequences/${sequenceId}/contacts/${contact.sc_id}/emails/${email.email_id}`,
                emailForm,
                { headers: getAuthHeader() }
            );
            setSeq(res.data);
            toast.success('Email saved');
        } catch { toast.error('Failed to save email'); }
    };

    const handleDeleteEmail = async () => {
        const contact = seq?.contacts?.[selectedContactIdx];
        const email = contact?.emails?.[selectedEmailIdx];
        if (!contact || !email) return;
        if (!window.confirm('Delete this email?')) return;
        try {
            const res = await axios.delete(
                `${API}/sequences/${sequenceId}/contacts/${contact.sc_id}/emails/${email.email_id}`,
                { headers: getAuthHeader() }
            );
            setSeq(res.data);
            setSelectedEmailIdx(Math.max(0, selectedEmailIdx - 1));
            syncEmailForm(res.data, selectedContactIdx, Math.max(0, selectedEmailIdx - 1));
            toast.success('Email deleted');
        } catch { toast.error('Failed to delete'); }
    };

    const [sending, setSending] = useState(false);
    const handleSendEmail = async () => {
        const contact = seq?.contacts?.[selectedContactIdx];
        const email = contact?.emails?.[selectedEmailIdx];
        if (!contact || !email) return;
        if (!email.subject?.trim()) return toast.error('Please add a subject before sending');
        if (!contact.contact_email) return toast.error('Contact has no email address');
        if (!window.confirm(`Send this email to ${contact.contact_name} (${contact.contact_email})?`)) return;
        setSending(true);
        try {
            const res = await axios.post(
                `${API}/sequences/${sequenceId}/contacts/${contact.sc_id}/emails/${email.email_id}/send`,
                {}, { headers: getAuthHeader() }
            );
            setSeq(res.data);
            syncEmailForm(res.data, selectedContactIdx, selectedEmailIdx);
            toast.success(`Email sent to ${contact.contact_email}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send email'); }
        finally { setSending(false); }
    };

    const handleDuplicate = async (targetScId) => {
        const contact = seq?.contacts?.[selectedContactIdx];
        const email = contact?.emails?.[selectedEmailIdx];
        if (!contact || !email) return;
        try {
            const res = await axios.post(
                `${API}/sequences/${sequenceId}/contacts/${contact.sc_id}/emails/${email.email_id}/duplicate`,
                { target_sc_id: targetScId },
                { headers: getAuthHeader() }
            );
            setSeq(res.data);
            setShowDuplicate(false);
            toast.success('Email duplicated');
        } catch { toast.error('Failed to duplicate'); }
    };

    // AI Suggestion handlers
    const handleApplySuggestion = async (suggestionId) => {
        const contact = seq?.contacts?.[selectedContactIdx];
        const email = contact?.emails?.[selectedEmailIdx];
        if (!contact || !email) return;
        try {
            const res = await axios.post(
                `${API}/sequences/${sequenceId}/contacts/${contact.sc_id}/emails/${email.email_id}/suggestions/${suggestionId}/apply`,
                {},
                { headers: getAuthHeader() }
            );
            setSeq(res.data);
            syncEmailForm(res.data, selectedContactIdx, selectedEmailIdx);
            toast.success('AI suggestion applied! Email updated with cross-contact intelligence.');
        } catch (err) { 
            toast.error(err.response?.data?.detail || 'Failed to apply suggestion'); 
        }
    };

    const handleDismissSuggestion = async (suggestionId) => {
        const contact = seq?.contacts?.[selectedContactIdx];
        const email = contact?.emails?.[selectedEmailIdx];
        if (!contact || !email) return;
        try {
            const res = await axios.post(
                `${API}/sequences/${sequenceId}/contacts/${contact.sc_id}/emails/${email.email_id}/suggestions/${suggestionId}/dismiss`,
                {},
                { headers: getAuthHeader() }
            );
            setSeq(res.data);
            toast.info('Suggestion dismissed');
        } catch (err) { 
            toast.error(err.response?.data?.detail || 'Failed to dismiss suggestion'); 
        }
    };

    const insertVariable = (varStr) => {
        setEmailForm(prev => ({ ...prev, body: prev.body + varStr }));
    };
    
    // Email template handlers
    const applyEmailTemplate = (template) => {
        setEmailForm(prev => ({
            ...prev,
            subject: template.subject,
            body: template.body
        }));
        setShowTemplatesBrowser(false);
        toast.success(`Template "${template.name}" applied`);
    };
    
    const getCategoryLabel = (value) => {
        const categories = {
            general: 'General',
            introduction: 'Introduction',
            follow_up: 'Follow-up',
            meeting: 'Meeting Request',
            proposal: 'Proposal',
            check_in: 'Check-in',
            thank_you: 'Thank You',
            cold_outreach: 'Cold Outreach',
        };
        return categories[value] || value;
    };
    
    const allTemplates = [...emailTemplates, ...adminEmailTemplates];
    const filteredTemplates = templateFilter === 'all'
        ? allTemplates
        : allTemplates.filter(t => t.category === templateFilter);
    
    // Signature handlers
    const resolveSigVars = (html) => {
        if (!html) return html;
        // v2.16.38: photo/logo use the permanent proxy endpoint so
        // campaign emails don't ship with 1h-expiring S3 URLs.
        const replacements = {
            '{{SenderName}}': user?.name || '',
            '{{SenderTitle}}': user?.title || '',
            '{{SenderCompany}}': user?.org_name || user?.company_name || '',
            '{{SenderEmail}}': user?.email || '',
            '{{SenderPhone}}': user?.phone || '',
            '{{SenderLinkedIn}}': user?.linkedin_url || '',
            '{{SenderAddress}}': user?.address || '',
            ...signatureAssetMergeMap(user),
        };
        Object.entries(replacements).forEach(([key, val]) => {
            html = html.split(key).join(val || '');
        });
        return html;
    };

    const insertSignature = (signature) => {
        const separator = emailForm.body ? '<p><br></p><p><br></p>' : '';
        setEmailForm(prev => ({
            ...prev,
            body: prev.body + separator + resolveSigVars(signature.content)
        }));
        toast.success(`Signature "${signature.name}" inserted`);
    };
    
    const handleSaveSignature = async () => {
        if (!signatureForm.name.trim() || !signatureForm.content.trim()) {
            toast.error('Please enter a name and content for the signature');
            return;
        }
        setSavingSignature(true);
        try {
            if (editingSignature) {
                // Update existing
                const res = await axios.put(
                    `${API}/signatures/${editingSignature.signature_id}`,
                    signatureForm,
                    { headers: getAuthHeader() }
                );
                setSignatures(prev => prev.map(s =>
                    s.signature_id === editingSignature.signature_id ? res.data : s
                ));
                toast.success('Signature updated');
            } else {
                // Create new
                const res = await axios.post(`${API}/signatures`, signatureForm, { headers: getAuthHeader() });
                setSignatures(prev => [...prev, res.data]);
                toast.success('Signature created');
            }
            setEditingSignature(null);
            setShowSignatureForm(false);
            setSignatureForm({ name: '', content: '', is_default: false });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save signature');
        } finally {
            setSavingSignature(false);
        }
    };
    
    const handleDeleteSignature = async (sigId) => {
        if (!window.confirm('Delete this signature?')) return;
        try {
            await axios.delete(`${API}/signatures/${sigId}`, { headers: getAuthHeader() });
            setSignatures(prev => prev.filter(s => s.signature_id !== sigId));
            toast.success('Signature deleted');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete signature');
        }
    };
    
    const handleCopyTemplate = async (templateId) => {
        try {
            const res = await axios.post(
                `${API}/signatures/from-template/${templateId}`,
                {},
                { headers: getAuthHeader() }
            );
            setSignatures(prev => [...prev, res.data]);
            toast.success('Template copied to your signatures');
            // Open the editor with the new signature
            setEditingSignature(res.data);
            setSignatureForm({
                name: res.data.name,
                content: res.data.content,
                is_default: res.data.is_default
            });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to copy template');
        }
    };
    
    const openEditSignature = (sig) => {
        setEditingSignature(sig);
        setSignatureForm({
            name: sig.name,
            content: sig.content,
            is_default: sig.is_default
        });
    };
    
    const resetSignatureForm = () => {
        setEditingSignature(null);
        setShowSignatureForm(false);
        setSignatureForm({ name: '', content: '', is_default: false });
    };
    
    const getDefaultSignature = () => signatures.find(s => s.is_default);

    const openAddContact = async () => {
        try {
            const res = await axios.get(`${API}/contacts`, { headers: getAuthHeader() });
            const existingIds = (seq?.contacts || []).map(c => c.contact_id);
            const contactArr = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            setCrmContacts(contactArr.filter(c => !existingIds.includes(c.contact_id)));
        } catch {}
        setShowAddContact(true);
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;
    if (!seq) return null;

    const currentContact = seq.contacts?.[selectedContactIdx];
    const currentEmail = currentContact?.emails?.[selectedEmailIdx];
    const totalEmails = (seq.contacts || []).reduce((sum, c) => sum + (c.emails || []).length, 0);

    return (
        <div className="h-[calc(100vh-8rem)] flex flex-col animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between px-1 pb-4 border-b border-stone-200 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/email-sequences')} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" data-testid="back-to-sequences">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <Input value={seq.name} onChange={e => setSeq(p => ({ ...p, name: e.target.value }))}
                            className="text-lg font-bold border-none shadow-none p-0 h-auto focus-visible:ring-0" style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="seq-name-edit" />
                        <p className="text-xs text-stone-400 mt-0.5">{seq.account_name} &middot; {(seq.contacts || []).length} contacts &middot; {totalEmails} emails</p>
                    </div>
                    {/* AI Insights Badge */}
                    {(() => {
                        const pendingSuggestions = (seq.contacts || []).reduce((count, c) => 
                            count + (c.emails || []).reduce((eCount, e) => 
                                eCount + (e.ai_suggestions || []).filter(s => s.status === 'pending').length, 0), 0);
                        const respondedContacts = (seq.contacts || []).filter(c => 
                            (c.emails || []).some(e => e.status === 'responded')).length;
                        if (pendingSuggestions > 0 || respondedContacts > 0) {
                            return (
                                <div className="flex items-center gap-2 ml-4 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                                    <Sparkles className="w-4 h-4 text-amber-600" />
                                    <div className="text-xs">
                                        {respondedContacts > 0 && (
                                            <span className="text-amber-700">{respondedContacts} response{respondedContacts !== 1 ? 's' : ''}</span>
                                        )}
                                        {pendingSuggestions > 0 && (
                                            <span className="text-amber-800 font-medium ml-1">• {pendingSuggestions} AI suggestion{pendingSuggestions !== 1 ? 's' : ''}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
                <div className="flex items-center gap-2">
                    {/* 7b-ii — Visibility control */}
                    {canEditVisibility ? (
                        <div className="flex items-center gap-1.5">
                            <select
                                value={seq.visibility || 'organization'}
                                onChange={e => handleVisibilityChange(e.target.value)}
                                disabled={savingVisibility}
                                className="h-8 text-xs border border-stone-200 rounded-md bg-white px-2 focus:outline-none focus:ring-1 focus:ring-stone-400 disabled:opacity-60"
                                data-testid="seq-visibility-select"
                                aria-label="Sequence visibility">
                                {SEQ_VISIBILITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {(seq.visibility === 'shared') && (
                                <button type="button" onClick={() => setShowSharePicker(true)}
                                    className="h-8 px-2 text-xs border border-stone-200 rounded-md text-stone-600 hover:bg-stone-50 whitespace-nowrap"
                                    data-testid="seq-share-with-btn">
                                    {(seq.shared_with || []).length} shared
                                </button>
                            )}
                        </div>
                    ) : (
                        <span className="flex items-center gap-1.5 h-8 px-2 text-xs text-stone-500 border border-stone-200 rounded-md bg-stone-50"
                            title="This sequence is locked — only its owner can change visibility">
                            <Lock className="w-3 h-3" /> {SEQ_VISIBILITY_LABEL[seq.visibility || 'organization']}
                        </span>
                    )}
                    <Select value={seq.status} onValueChange={v => setSeq(p => ({ ...p, status: v }))}>
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid="seq-status-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => navigate('/email-sequences')}>Cancel</Button>
                    <Button onClick={handleSaveSequence} disabled={saving} className="bg-stone-900 hover:bg-stone-800" data-testid="save-seq-btn">
                        <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {/* 3-Panel Layout */}
            <div className="flex flex-1 min-h-0 mt-4 gap-4">
                {/* Left Panel: Contacts */}
                <div className="w-56 flex-shrink-0 bg-white rounded-xl border border-stone-200 flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-stone-200 flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Contacts</h3>
                        <button onClick={openAddContact} className="p-1.5 hover:bg-stone-100 rounded text-stone-700" data-testid="add-contact-btn">
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {(seq.contacts || []).length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="w-6 h-6 mx-auto text-stone-300 mb-2" />
                                <p className="text-xs text-stone-400 mb-2">No contacts yet</p>
                                <Button size="sm" variant="outline" onClick={openAddContact} className="text-xs">Add Contact</Button>
                            </div>
                        ) : (
                            (seq.contacts || []).map((contact, idx) => {
                                const emailStatuses = (contact.emails || []).map(e => e.status);
                                const hasResponse = emailStatuses.includes('responded');
                                const hasSent = emailStatuses.includes('sent') || emailStatuses.includes('opened');
                                const hasPendingSuggestions = (contact.emails || []).some(e => 
                                    (e.ai_suggestions || []).some(s => s.status === 'pending'));
                                return (
                                    <div key={contact.sc_id}
                                        onClick={() => selectContact(idx)}
                                        className={`p-2.5 rounded-lg cursor-pointer transition-colors group ${selectedContactIdx === idx ? 'bg-stone-100 border border-stone-300' : 'hover:bg-stone-50 border border-transparent'}`}
                                        data-testid={`contact-item-${idx}`}>
                                        <div className="flex items-start justify-between">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <p className="text-sm font-medium text-stone-800 truncate">{contact.contact_name}</p>
                                                    {hasPendingSuggestions && (
                                                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" title="Has AI suggestions" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-stone-400 truncate">{contact.contact_title}</p>
                                                <div className="flex items-center gap-1 mt-1">
                                                    <span className="text-xs text-stone-400">{(contact.emails || []).length} emails</span>
                                                    {hasResponse && <MessageSquare className="w-3 h-3 text-violet-500" />}
                                                    {hasSent && !hasResponse && <CheckCircle className="w-3 h-3 text-blue-500" />}
                                                    {!hasSent && !hasResponse && <Clock className="w-3 h-3 text-stone-300" />}
                                                </div>
                                            </div>
                                            <button onClick={(e) => { e.stopPropagation(); handleRemoveContact(contact.sc_id); }}
                                                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Center Panel: Email Timeline */}
                <div className="w-64 flex-shrink-0 bg-white rounded-xl border border-stone-200 flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-stone-200 flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                            {currentContact ? `${currentContact.contact_name.split(' ')[0]}'s Emails` : 'Emails'}
                        </h3>
                        {currentContact && (
                            <button onClick={handleAddEmail} className="p-1.5 hover:bg-stone-100 rounded text-stone-700" data-testid="add-email-btn">
                                <Plus className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {!currentContact ? (
                            <p className="text-xs text-stone-400 text-center py-8">Select a contact to view emails</p>
                        ) : (currentContact.emails || []).length === 0 ? (
                            <div className="text-center py-8">
                                <Mail className="w-6 h-6 mx-auto text-stone-300 mb-2" />
                                <p className="text-xs text-stone-400 mb-2">No emails yet</p>
                                <Button size="sm" variant="outline" onClick={handleAddEmail} className="text-xs">Add Email</Button>
                            </div>
                        ) : (
                            (currentContact.emails || []).map((email, idx) => {
                                const StatusIcon = emailStatusIcons[email.status] || Clock;
                                const hasPendingSuggestions = (email.ai_suggestions || []).some(s => s.status === 'pending');
                                return (
                                    <div key={email.email_id}
                                        onClick={() => selectEmail(idx)}
                                        className={`p-3 rounded-lg cursor-pointer transition-colors border ${selectedEmailIdx === idx ? 'bg-stone-100 border-stone-300' : 'hover:bg-stone-50 border-stone-100'}`}
                                        data-testid={`email-card-${idx}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-bold text-stone-700">#{email.email_number}</span>
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${emailStatusColors[email.status] || emailStatusColors.scheduled}`}>
                                                {email.status}
                                            </span>
                                            {hasPendingSuggestions && (
                                                <Sparkles className="w-3 h-3 text-amber-500" />
                                            )}
                                        </div>
                                        <p className="text-sm text-stone-800 truncate font-medium">{email.subject || '(No subject)'}</p>
                                        {email.scheduled_date && (
                                            <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                                                <Clock className="w-3 h-3" />{email.scheduled_date}
                                            </p>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Right Panel: Email Composer */}
                <div className="flex-1 bg-white rounded-xl border border-stone-200 flex flex-col overflow-hidden">
                    {!currentEmail ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <Mail className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                                <p className="text-stone-500">Select or create an email to edit</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Composer Header */}
                            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-stone-700">Email #{currentEmail.email_number}</span>
                                    <span className="text-xs text-stone-400">to {currentContact.contact_name}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="h-7 text-xs"
                                        onClick={() => setShowTemplatesBrowser(true)}
                                        data-testid="use-template-btn"
                                    >
                                        <Layers className="w-3 h-3 mr-1" />Use Template
                                    </Button>
                                    <Select value="" onValueChange={v => insertVariable(v)}>
                                        <SelectTrigger className="w-36 h-7 text-xs">
                                            <Variable className="w-3 h-3 mr-1" /><SelectValue placeholder="Insert Variable" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MERGE_VARS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowDuplicate(true)} data-testid="duplicate-email-btn">
                                        <Copy className="w-3 h-3 mr-1" />Duplicate
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50" onClick={handleDeleteEmail}>
                                        <Trash2 className="w-3 h-3 mr-1" />Delete
                                    </Button>
                                </div>
                            </div>

                            {/* Composer Body */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                <div>
                                    <Label className="text-xs">Subject Line</Label>
                                    <Input value={emailForm.subject} onChange={e => setEmailForm(p => ({ ...p, subject: e.target.value }))}
                                        placeholder="Enter email subject..." className="mt-1" data-testid="email-subject-input" />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label className="text-xs">Email Body</Label>
                                        <div className="flex items-center gap-1">
                                            {/* Signature dropdown */}
                                            <Select value="" onValueChange={sigId => {
                                                const sig = [...signatures, ...adminTemplates].find(s => s.signature_id === sigId);
                                                if (sig) insertSignature(sig);
                                            }}>
                                                <SelectTrigger className="w-36 h-7 text-xs" data-testid="signature-selector">
                                                    <FileSignature className="w-3 h-3 mr-1" />
                                                    <SelectValue placeholder="Add Signature" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {signatures.length > 0 && (
                                                        <>
                                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400">My Signatures</div>
                                                            {signatures.map(s => (
                                                                <SelectItem key={s.signature_id} value={s.signature_id}>
                                                                    {s.is_default && <Star className="w-3 h-3 inline mr-1 text-amber-500" />}
                                                                    {s.name}
                                                                </SelectItem>
                                                            ))}
                                                        </>
                                                    )}
                                                    {adminTemplates.length > 0 && (
                                                        <>
                                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1">Templates</div>
                                                            {adminTemplates.map(t => (
                                                                <SelectItem key={t.signature_id} value={t.signature_id}>{t.name}</SelectItem>
                                                            ))}
                                                        </>
                                                    )}
                                                    {signatures.length === 0 && adminTemplates.length === 0 && (
                                                        <div className="px-2 py-2 text-xs text-stone-400">No signatures yet</div>
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 text-xs"
                                                onClick={() => setShowSignatureManager(true)}
                                                data-testid="manage-signatures-btn"
                                            >
                                                <Settings2 className="w-3 h-3 mr-1" />Manage
                                            </Button>
                                        </div>
                                    </div>
                                    <div data-testid="email-body-editor">
                                        <RichTextEditor
                                            content={emailForm.body}
                                            onChange={(html) => setEmailForm(p => ({ ...p, body: html }))}
                                            placeholder="Compose your email here..."
                                            minHeight="180px"
                                            showMergeVariables={true}
                                            showAIWriter={true}
                                            showContentLibrary={true}
                                            onSubjectGenerated={(subj) => setEmailForm(p => ({ ...p, subject: subj }))}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs">Scheduled Date</Label>
                                        <Input type="date" value={emailForm.scheduled_date} onChange={e => setEmailForm(p => ({ ...p, scheduled_date: e.target.value }))}
                                            className="mt-1" data-testid="email-date-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Status</Label>
                                        <Select value={emailForm.status} onValueChange={v => setEmailForm(p => ({ ...p, status: v }))}>
                                            <SelectTrigger className="mt-1" data-testid="email-status-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="scheduled">Scheduled</SelectItem>
                                                <SelectItem value="sent">Sent</SelectItem>
                                                <SelectItem value="opened">Opened</SelectItem>
                                                <SelectItem value="responded">Responded</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Email Activity Log */}
                                {(currentEmail.engagement_events || []).length > 0 && (
                                    <div className="border border-stone-200 rounded-lg p-3">
                                        <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Email Activity</h4>
                                        <div className="space-y-1.5">
                                            {currentEmail.engagement_events.map((ev, i) => (
                                                <div key={i} className="flex items-center gap-2 text-xs text-stone-500">
                                                    <span className={`w-1.5 h-1.5 rounded-full ${ev.event_type === 'responded' ? 'bg-violet-500' : ev.event_type === 'opened' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                                                    <span className="capitalize font-medium">{ev.event_type}</span>
                                                    <span className="text-stone-400">{new Date(ev.timestamp).toLocaleString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* AI Suggestions on this email */}
                                {(currentEmail.ai_suggestions || []).filter(s => s.status === 'pending').length > 0 && (
                                    <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                                                <Sparkles className="w-4 h-4 text-amber-600" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-semibold text-amber-900">AI Cross-Contact Intelligence</h4>
                                                <p className="text-xs text-amber-700">A colleague at this company responded — here's a suggested rewrite</p>
                                            </div>
                                        </div>
                                        {(currentEmail.ai_suggestions || []).filter(s => s.status === 'pending').map(sug => (
                                            <div key={sug.suggestion_id} className="space-y-3">
                                                <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-100 rounded-md px-2 py-1">
                                                    <MessageSquare className="w-3 h-3" />
                                                    <span><strong>{sug.trigger_contact_name}</strong> responded to your outreach</span>
                                                </div>
                                                <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-xs font-medium text-stone-500">SUGGESTED REWRITE</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-stone-800 mb-1">Subject: {sug.suggested_subject}</p>
                                                    <div className="text-sm text-stone-600 whitespace-pre-wrap border-t border-stone-100 pt-2 mt-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sug.suggested_body) }} />
                                                </div>
                                                <p className="text-xs text-amber-700 italic">{sug.reasoning}</p>
                                                <div className="flex items-center gap-2 pt-1">
                                                    <Button 
                                                        size="sm" 
                                                        className="bg-amber-600 hover:bg-amber-700 h-7 text-xs"
                                                        onClick={() => handleApplySuggestion(sug.suggestion_id)}
                                                        data-testid={`apply-suggestion-${sug.suggestion_id}`}
                                                    >
                                                        <CheckCircle className="w-3 h-3 mr-1" /> Apply Suggestion
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                                                        onClick={() => handleDismissSuggestion(sug.suggestion_id)}
                                                        data-testid={`dismiss-suggestion-${sug.suggestion_id}`}
                                                    >
                                                        <X className="w-3 h-3 mr-1" /> Dismiss
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Save & Send Buttons */}
                            <div className="p-4 border-t border-stone-200 flex gap-2">
                                <Button onClick={handleSaveEmail} className="flex-1 bg-stone-900 hover:bg-stone-800" data-testid="save-email-btn">
                                    Save Email
                                </Button>
                                {currentEmail.status === 'scheduled' && (
                                    <Button onClick={handleSendEmail} disabled={sending} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="send-email-btn">
                                        <Send className="w-4 h-4 mr-1.5" />{sending ? 'Sending...' : 'Send Now'}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Add Contact Dialog */}
            <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Contact to Sequence</DialogTitle>
                        <DialogDescription>Select a contact from your CRM</DialogDescription>
                    </DialogHeader>
                    <div className="max-h-64 overflow-y-auto space-y-1 py-2">
                        {crmContacts.length === 0 ? (
                            <p className="text-sm text-stone-400 text-center py-4">No available contacts</p>
                        ) : crmContacts.map(c => (
                            <button key={c.contact_id} onClick={() => handleAddContact(c.contact_id)}
                                className="w-full text-left p-3 hover:bg-stone-100 rounded-lg transition-colors" data-testid={`crm-contact-${c.contact_id}`}>
                                <p className="text-sm font-medium text-stone-800">{c.name}</p>
                                <p className="text-xs text-stone-400">{c.title} &middot; {c.email}</p>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Duplicate Dialog */}
            <Dialog open={showDuplicate} onOpenChange={setShowDuplicate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Duplicate Email to Contact</DialogTitle>
                        <DialogDescription>Choose a contact to duplicate this email to</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1 py-2">
                        {(seq.contacts || []).filter((_, i) => i !== selectedContactIdx).map(c => (
                            <button key={c.sc_id} onClick={() => handleDuplicate(c.sc_id)}
                                className="w-full text-left p-3 hover:bg-stone-100 rounded-lg transition-colors">
                                <p className="text-sm font-medium text-stone-800">{c.contact_name}</p>
                                <p className="text-xs text-stone-400">{c.contact_title}</p>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Signature Manager Dialog */}
            <Dialog open={showSignatureManager} onOpenChange={(open) => {
                setShowSignatureManager(open);
                if (!open) resetSignatureForm();
            }}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSignature className="w-5 h-5 text-stone-700" />
                            Email Signatures
                        </DialogTitle>
                        <DialogDescription>
                            Create and manage your email signatures. Set one as default to auto-include in new emails.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs defaultValue="my-signatures" className="flex-1 overflow-hidden flex flex-col">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="my-signatures">My Signatures ({signatures.length})</TabsTrigger>
                            <TabsTrigger value="templates">Templates ({adminTemplates.length})</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="my-signatures" className="flex-1 overflow-hidden flex flex-col mt-4">
                            {editingSignature || showSignatureForm ? (
                                /* Edit/Create Form */
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-medium">
                                            {editingSignature ? 'Edit Signature' : 'New Signature'}
                                        </h4>
                                        <Button variant="ghost" size="sm" onClick={resetSignatureForm}>
                                            <X className="w-4 h-4 mr-1" /> Cancel
                                        </Button>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Signature Name</Label>
                                        <Input
                                            value={signatureForm.name}
                                            onChange={e => setSignatureForm(p => ({ ...p, name: e.target.value }))}
                                            placeholder="e.g., Professional, Casual, Sales..."
                                            className="mt-1"
                                            data-testid="signature-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Signature Content</Label>
                                        <div className="mt-1" data-testid="signature-content-editor">
                                            <RichTextEditor
                                                content={signatureForm.content}
                                                onChange={(html) => setSignatureForm(p => ({ ...p, content: html }))}
                                                placeholder="Design your signature here... Include name, title, contact info, social links, etc."
                                                minHeight="120px"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={signatureForm.is_default}
                                                onChange={e => setSignatureForm(p => ({ ...p, is_default: e.target.checked }))}
                                                className="rounded border-stone-300"
                                            />
                                            <span>Set as default signature</span>
                                            {signatureForm.is_default && <Star className="w-4 h-4 text-amber-500" />}
                                        </label>
                                        <Button onClick={handleSaveSignature} disabled={savingSignature} data-testid="save-signature-btn">
                                            <Save className="w-4 h-4 mr-1" />
                                            {savingSignature ? 'Saving...' : 'Save Signature'}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                /* Signatures List */
                                <div className="flex-1 overflow-y-auto space-y-2">
                                    {signatures.length === 0 ? (
                                        <div className="text-center py-8">
                                            <FileSignature className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                                            <p className="text-sm text-stone-500 mb-3">No signatures yet</p>
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setShowSignatureForm(true);
                                                    setSignatureForm({ name: '', content: '', is_default: false });
                                                }}
                                                data-testid="create-first-signature-btn"
                                            >
                                                <Plus className="w-4 h-4 mr-1" /> Create Your First Signature
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="w-full mb-2"
                                                onClick={() => {
                                                    setShowSignatureForm(true);
                                                    setSignatureForm({ name: '', content: '', is_default: false });
                                                }}
                                                data-testid="create-new-signature-btn"
                                            >
                                                <Plus className="w-4 h-4 mr-1" /> Create New Signature
                                            </Button>
                                            {signatures.map(sig => (
                                                <div
                                                    key={sig.signature_id}
                                                    className="border border-stone-200 rounded-lg p-3 hover:border-stone-300 transition-colors"
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-sm">{sig.name}</span>
                                                            {sig.is_default && (
                                                                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                                                    <Star className="w-3 h-3" /> Default
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7"
                                                                onClick={() => openEditSignature(sig)}
                                                            >
                                                                <PenLine className="w-3 h-3 mr-1" /> Edit
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                onClick={() => handleDeleteSignature(sig.signature_id)}
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="text-xs text-stone-500 line-clamp-2 prose prose-sm max-w-none"
                                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sig.content) }}
                                                    />
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </TabsContent>
                        
                        <TabsContent value="templates" className="flex-1 overflow-y-auto mt-4">
                            {adminTemplates.length === 0 ? (
                                <div className="text-center py-8">
                                    <FileSignature className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                                    <p className="text-sm text-stone-500">No templates available</p>
                                    <p className="text-xs text-stone-400 mt-1">Ask your admin to create signature templates</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-xs text-stone-500 mb-3">
                                        Use these admin-provided templates as a starting point. Click "Use Template" to copy and customize.
                                    </p>
                                    {adminTemplates.map(template => (
                                        <div
                                            key={template.signature_id}
                                            className="border border-stone-200 rounded-lg p-3 hover:border-stone-300 transition-colors"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium text-sm">{template.name}</span>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7"
                                                    onClick={() => handleCopyTemplate(template.signature_id)}
                                                    data-testid={`use-template-${template.signature_id}`}
                                                >
                                                    <Copy className="w-3 h-3 mr-1" /> Use Template
                                                </Button>
                                            </div>
                                            <div
                                                className="text-xs text-stone-500 line-clamp-3 prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.content) }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </DialogContent>
            </Dialog>
            
            {/* Email Templates Browser Dialog */}
            <Dialog open={showTemplatesBrowser} onOpenChange={setShowTemplatesBrowser}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-stone-700" />
                            Email Templates
                        </DialogTitle>
                        <DialogDescription>
                            Choose a template to use as the starting point for your email. This will replace the current subject and body.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {/* Category Filter */}
                    <div className="flex items-center gap-2 py-2 border-b border-stone-100">
                        <span className="text-sm text-stone-500">Filter:</span>
                        <Select value={templateFilter} onValueChange={setTemplateFilter}>
                            <SelectTrigger className="w-40 h-8">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                <SelectItem value="introduction">Introduction</SelectItem>
                                <SelectItem value="follow_up">Follow-up</SelectItem>
                                <SelectItem value="meeting">Meeting Request</SelectItem>
                                <SelectItem value="proposal">Proposal</SelectItem>
                                <SelectItem value="check_in">Check-in</SelectItem>
                                <SelectItem value="thank_you">Thank You</SelectItem>
                                <SelectItem value="cold_outreach">Cold Outreach</SelectItem>
                                <SelectItem value="general">General</SelectItem>
                            </SelectContent>
                        </Select>
                        <span className="text-xs text-stone-400">
                            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    
                    {/* Templates List */}
                    <div className="flex-1 overflow-y-auto py-2 space-y-2">
                        {allTemplates.length === 0 ? (
                            <div className="text-center py-8">
                                <FileText className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                                <p className="text-sm text-stone-500">No email templates available</p>
                                <p className="text-xs text-stone-400 mt-1">Ask your admin to create email templates</p>
                            </div>
                        ) : filteredTemplates.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-sm text-stone-500">No templates in this category</p>
                            </div>
                        ) : (
                            filteredTemplates.map(template => (
                                <div
                                    key={template.template_id}
                                    className="border border-stone-200 rounded-lg p-3 hover:border-stone-400 hover:bg-stone-100/30 transition-colors cursor-pointer group"
                                    onClick={() => applyEmailTemplate(template)}
                                    data-testid={`apply-template-${template.template_id}`}
                                >
                                    <div className="flex items-start justify-between mb-1">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm text-stone-800">{template.name}</span>
                                                <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">
                                                    {getCategoryLabel(template.category)}
                                                </span>
                                                {template.is_admin_template && (
                                                    <span className="text-xs bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">
                                                        Admin
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-stone-600 mt-1 font-medium">
                                                Subject: {template.subject}
                                            </p>
                                        </div>
                                        <Button
                                            size="sm"
                                            className="h-7 opacity-0 group-hover:opacity-100 transition-opacity bg-stone-900 hover:bg-stone-800"
                                        >
                                            Use
                                        </Button>
                                    </div>
                                    <div
                                        className="text-xs text-stone-500 line-clamp-2 mt-2 prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.body) }}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* 7b-ii — Shared-with member picker */}
            <Dialog open={showSharePicker} onOpenChange={setShowSharePicker}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Share sequence</DialogTitle>
                        <DialogDescription>
                            Pick the teammates who can see this sequence. They'll have access in addition to you.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-72 overflow-y-auto border border-stone-200 rounded-lg p-2 space-y-1">
                        {orgMembers.filter(m => m.user_id !== user?.user_id).length === 0 ? (
                            <p className="text-sm text-stone-400 py-2 text-center">No teammates found</p>
                        ) : (
                            orgMembers
                                .filter(m => m.user_id !== user?.user_id)
                                .map(m => (
                                    <label key={m.user_id} className="flex items-center gap-2 text-sm text-stone-700 px-1.5 py-1 rounded hover:bg-stone-50 cursor-pointer">
                                        <input type="checkbox"
                                            checked={(seq.shared_with || []).includes(m.user_id)}
                                            onChange={() => toggleShareMember(m.user_id)}
                                            disabled={savingVisibility}
                                            className="rounded border-stone-300" />
                                        <span className="truncate">{m.name || m.email}</span>
                                    </label>
                                ))
                        )}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setShowSharePicker(false)} className="bg-stone-900 hover:bg-stone-800">Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
